/**
 * @fileoverview Reference project picker for the "Edit Like Me" feature.
 * Renders a searchable dropdown of the user's previously-edited projects.
 */

import React, { useState, useEffect } from 'react';
import TrebbleSelect from '../../common/TrebbleSelect.tsx';
import TrebbleTooltip from '../../common/TrebbleTooltip.tsx';
import { TrebbleText } from '../../common/TrebbleTypography.tsx';
import { InfoCircleOutlined } from '@ant-design/icons';
// @ts-ignore
import ti18n from 'i18n!nls/Deepcuti18n';

const { Option } = TrebbleSelect;

/**
 * @typedef {Object} ReferenceProjectData
 * @property {string} projectId
 * @property {string|null} name
 * @property {string|null} textTranscription - First 200 chars of transcription
 * @property {string|null} lastEditedDate
 * @property {number} version
 * @property {string[]} transcriptionIds
 * @property {boolean} hasAddedMedia
 */

/**
 * Fetches the user's edited projects from the backend.
 * @param {string} [searchKeyword]
 * @returns {Promise<ReferenceProjectData[]>}
 */
async function fetchEditedProjects(searchKeyword) {
  try {
    const TrebbleClientAPI = (await import(/* webpackChunkName: "TrebbleClientAPI" */ '../../../models/helper/TrebbleClientAPI')).default;
    const api = TrebbleClientAPI.getInstance();
    const result = await api.getEditedProjectsForUser(50, 0, searchKeyword || '');
    return result?.projects || [];
  } catch (error) {
    console.error('Failed to fetch edited projects:', error);
    return [];
  }
}

/**
 * Reference Project Picker component
 * @param {Object} props
 * @param {string|null} props.value - Selected project ID
 * @param {Function} props.onChange - (projectId, projectData) => void
 * @param {boolean} props.required - Whether selection is required
 * @param {boolean} [props.disabled]
 */
export default function ReferenceProjectPicker({
  value,
  onChange,
  required = false,
  disabled = false,
  prefetchedProjects = null,
}) {
  const [projects, setProjects] = useState(prefetchedProjects || []);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(!!prefetchedProjects);

  // Use prefetched projects when they arrive
  useEffect(() => {
    if (prefetchedProjects && !loaded) {
      setProjects(prefetchedProjects);
      setLoaded(true);
      setLoading(false);
    }
  }, [prefetchedProjects, loaded]);

  // Fallback: fetch if no prefetched data provided
  useEffect(() => {
    if (!loaded && !loading && !prefetchedProjects) {
      setLoading(true);
      fetchEditedProjects().then((result) => {
        setProjects(result);
        setLoading(false);
        setLoaded(true);
      });
    }
  }, [loaded, loading, prefetchedProjects]);

  const handleChange = (selectedProjectId) => {
    if (!selectedProjectId) {
      onChange(null, null);
      return;
    }
    const projectData = projects.find((p) => p.projectId === selectedProjectId);
    onChange(selectedProjectId, projectData || null);
  };

  const getProjectLabel = (project) => {
    if (project.name) return project.name;
    if (project.textTranscription) {
      const preview = project.textTranscription.substring(0, 80);
      return preview + (project.textTranscription.length > 80 ? '...' : '');
    }
    return `Project ${project.projectId.substring(0, 8)}...`;
  };

  return (
    <div className="mb-5">
      <TrebbleText strong className="mb-3 flex items-center gap-2 text-base">
        {window.getI18n(ti18n, 'REFERENCE_PROJECT_LABEL') || 'Reference Project'}
        {required && <span style={{ color: 'var(--trebble-error, #ff4d4f)' }}>*</span>}
        <TrebbleTooltip
          title={window.getI18n(ti18n, 'REFERENCE_PROJECT_TOOLTIP') || "We'll analyze how this project was edited and apply the same style to your new content."}
          placement="right"
        >
          <InfoCircleOutlined style={{ color: 'var(--trebble-primary)', fontSize: 16, marginLeft: 4 }} />
        </TrebbleTooltip>
      </TrebbleText>
      <TrebbleSelect
        value={value || undefined}
        onChange={handleChange}
        className="w-full"
        placeholder={
          loading
            ? (window.getI18n(ti18n, 'REFERENCE_PROJECT_LOADING') || 'Loading your projects...')
            : (window.getI18n(ti18n, 'REFERENCE_PROJECT_PLACEHOLDER') || 'Search your projects...')
        }
        allowClear
        showSearch
        disabled={disabled || loading}
        loading={loading}
        filterOption={(input, option) => {
          const label = option?.label || '';
          return label.toLowerCase().includes(input.toLowerCase());
        }}
        optionLabelProp="label"
        notFoundContent={
          loading
            ? (window.getI18n(ti18n, 'REFERENCE_PROJECT_LOADING') || 'Loading...')
            : (window.getI18n(ti18n, 'NO_PROJECTS_FOUND') || 'No previously edited projects found.')
        }
      >
        {[...new Map(projects.map((p) => [p.projectId, p])).values()].map((project) => {
          const label = getProjectLabel(project);
          return (
            <Option key={project.projectId} value={project.projectId} label={label}>
              <div className="py-1">
                <TrebbleText strong size="small" className="text-sm">
                  {label}
                </TrebbleText>
                {project.lastEditedDate && (
                  <>
                    <br />
                    <TrebbleText type="secondary" size="small" className="text-xs mt-0.5 block">
                      {`v${project.version} — ${new Date(project.lastEditedDate).toLocaleDateString()}`}
                      {project.hasAddedMedia ? ' — has media' : ''}
                    </TrebbleText>
                  </>
                )}
              </div>
            </Option>
          );
        })}
      </TrebbleSelect>
      {required && !value && (
        <TrebbleText type="danger" size="small" className="text-xs mt-1 block">
          {window.getI18n(ti18n, 'REFERENCE_PROJECT_REQUIRED') || 'Please select a reference project to continue.'}
        </TrebbleText>
      )}
    </div>
  );
}
